import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../../components/ui/dialog";
import { Wrench } from "lucide-react";

const OUTBOARD_BRANDS = new Set([
  "yamaha", "mercury", "mercury marine", "honda", "suzuki", "tohatsu",
  "evinrude", "rotax", "textron",
]);
const isOutboard = (e) => OUTBOARD_BRANDS.has((e?.brand || "").trim().toLowerCase());

const label = (hours, days) => {
  if (hours && days) return `every ${hours} h or ${days >= 365 ? `${Math.round(days / 365 * 12)} months` : `${days} days`}, whichever first`;
  if (hours) return `every ${hours} h`;
  return days >= 365 ? `every ${Math.round(days / 365 * 12)} months` : `every ${days} days`;
};

/** Build the suggested service-plan entries from the yacht's machinery. */
export function buildServicePlan(yacht) {
  const engines = yacht?.engines || [];
  const generators = yacht?.generators || [];
  const stabilizers = yacht?.stabilizers || [];
  const tenders = yacht?.tenders || [];
  const out = [];

  if (engines.length) {
    const groups = engines.map((e) => (e.group || "").trim()).filter(Boolean);
    const grp = groups.length
      ? groups.sort((a, b) => groups.filter((g) => g === b).length - groups.filter((g) => g === a).length)[0]
      : null;
    const outboard = engines.every(isOutboard);
    // Anchor the hours counter at the current highest engine hours so the
    // first service comes due one full interval from NOW.
    const hoursNow = Math.max(0, ...engines.map((e) => Number(e.hours) || 0));
    const items = outboard
      ? [
          ["Engine oil & filter change", 100, 365],
          ["Gear (lower unit) oil change", 200, 365],
          ["Water pump impeller", 300, 365],
          ["Spark plugs", 200, 365],
        ]
      : [
          ["Engine oil & filter change", 250, 365],
          ["Raw water pump impeller", 500, 365],
          ["Fuel filters (primary & secondary)", 500, 365],
          ["Gearbox / pod oil change", 1000, 730],
        ];
    for (const [t, hours, days] of items) {
      out.push({
        section: "Engines",
        title: `Main engines — ${t.toLowerCase()}`,
        interval: label(hours, days),
        payload: grp
          ? {
              recurrence_kind: "engine_hours", recurrence_value: hours, secondary_value: days,
              engine_target_kind: "group", engine_group: grp,
              next_due_hours: hoursNow + hours,
            }
          : { recurrence_kind: "days", recurrence_value: days },
        system_category: "Engines & Propulsion",
        description: `${t} for all main engines. Typical interval: ${label(hours, days)}.${grp ? "" : ` Suggested: every ${hours} engine hours.`}`,
      });
    }
  }

  if (generators.length) {
    for (const [t, hours, days] of [
      ["Oil & filter change", 250, 365],
      ["Raw water pump impeller", null, 365],
      ["Fuel filters", 500, 365],
    ]) {
      out.push({
        section: "Generators",
        title: `Generators — ${t.toLowerCase()}`,
        interval: label(null, days),
        payload: { recurrence_kind: "days", recurrence_value: days },
        system_category: "Generators",
        description: `${t} for all generators.${hours ? ` Also due after ~${hours} generator hours.` : ""}`,
      });
    }
  }

  if (stabilizers.length) {
    const gyro = stabilizers.some((s) => (s.brand || "").toLowerCase().includes("seakeeper"));
    out.push({
      section: "Stabilizers",
      title: gyro ? "Stabilizers — Seakeeper annual service" : "Stabilizers — annual service & seals check",
      interval: label(null, 365),
      payload: { recurrence_kind: "days", recurrence_value: 365 },
      system_category: "Stabilizers",
      description: gyro
        ? "Annual Seakeeper service (zincs, coolant, belt inspection)."
        : "Annual fin stabilizer service — seals, hydraulic oil and anodes.",
    });
  }

  tenders.forEach((t, i) => {
    const name = (t.name || "").trim() || `Tender ${i + 1}`;
    for (const [tt, hours] of [
      ["engine oil & filter change", 100],
      ["gear (lower unit) oil change", 200],
      ["water pump impeller", 300],
    ]) {
      out.push({
        section: "Tenders",
        title: `${name} — ${tt}`,
        interval: label(null, 365),
        payload: { recurrence_kind: "days", recurrence_value: 365 },
        system_category: "Tenders & Toys",
        description: `${tt[0].toUpperCase()}${tt.slice(1)} for ${name}. Also due after ~${hours} engine hours.`,
      });
    }
  });

  return out;
}

export function ServicePlanDialog({ yachtId, existingTitles, open, onClose, onCreated }) {
  const [yacht, setYacht] = useState(null);
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !yachtId) return;
    setYacht(null);
    api.get(`/yachts/${yachtId}`).then((r) => setYacht(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [open, yachtId]);

  const plan = useMemo(() => (yacht ? buildServicePlan(yacht) : []), [yacht]);
  const existing = useMemo(() => new Set([...(existingTitles || [])].map((t) => t.toLowerCase())), [existingTitles]);

  useEffect(() => {
    const init = {};
    plan.forEach((p, i) => { init[i] = !existing.has(p.title.toLowerCase()); });
    setChecked(init);
  }, [plan, existing]);

  const sections = useMemo(() => {
    const by = {};
    plan.forEach((p, i) => { (by[p.section] = by[p.section] || []).push({ ...p, idx: i }); });
    return by;
  }, [plan]);

  const selectedCount = Object.values(checked).filter(Boolean).length;

  const create = async () => {
    setBusy(true);
    let created = 0;
    try {
      for (let i = 0; i < plan.length; i++) {
        if (!checked[i]) continue;
        const p = plan[i];
        await api.post("/routine-maintenance", {
          yacht_id: yachtId,
          title: p.title,
          system_category: p.system_category,
          system_categories: [p.system_category],
          priority: "medium",
          description: p.description,
          ...p.payload,
        });
        created += 1;
      }
      toast.success(`Created ${created} periodic service task${created === 1 ? "" : "s"}.`);
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(fmtErr(e));
      if (created) onCreated?.();
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="service-plan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wrench className="h-4 w-4" /> Suggested service plan</DialogTitle>
          <DialogDescription>
            Based on this yacht&apos;s machinery. Untick anything you don&apos;t want — every task stays fully editable afterwards.
          </DialogDescription>
        </DialogHeader>
        {!yacht ? (
          <div className="py-6 text-center text-sm text-zinc-500">Loading machinery…</div>
        ) : plan.length === 0 ? (
          <div className="py-6 text-center text-sm text-zinc-500" data-testid="service-plan-empty">
            No engines, generators, stabilizers or tenders found on this yacht — add them in the Yachts menu first.
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(sections).map(([section, items]) => (
              <div key={section}>
                <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-500 mb-1.5">{section}</div>
                <div className="space-y-1.5">
                  {items.map((p) => {
                    const dup = existing.has(p.title.toLowerCase());
                    return (
                      <label
                        key={p.idx}
                        className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${dup ? "border-zinc-100 bg-zinc-50 text-zinc-400" : "border-zinc-200 bg-white cursor-pointer hover:border-zinc-400"}`}
                        data-testid={`service-plan-item-${p.idx}`}
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 accent-black"
                          disabled={dup || busy}
                          checked={!!checked[p.idx] && !dup}
                          onChange={(e) => setChecked((c) => ({ ...c, [p.idx]: e.target.checked }))}
                          data-testid={`service-plan-check-${p.idx}`}
                        />
                        <span className="min-w-0">
                          <span className="block leading-snug">{p.title}</span>
                          <span className="block text-[11px] text-zinc-500">{dup ? "already exists" : p.interval}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            className="bg-ink hover:bg-black"
            onClick={create}
            disabled={busy || selectedCount === 0}
            data-testid="service-plan-create-btn"
          >
            {busy ? "Creating…" : `Create ${selectedCount} task${selectedCount === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
